import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SEO } from '../components/SEO';
import { useLanguageStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { getRecaptchaToken } from '../lib/recaptcha';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';

const toSafeStr = (val, lang = 'en') => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  const localized = getLocalized(val, lang);
  if (typeof localized === 'object' && localized !== null) {
    const firstStr = Object.values(localized).find((v) => typeof v === 'string');
    return firstStr || '';
  }
  return String(localized || '');
};

const OfficeMap = ({ lat, lng, city, address }) => {
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    setMapReady(false);
    const fallbackTimer = setTimeout(() => {
      setMapReady(true);
    }, 1500);
    return () => clearTimeout(fallbackTimer);
  }, [lat, lng, address]);

  let src = null;
  if (lat && lng) {
    src = `https://maps.google.com/maps?q=${lat},${lng}&z=14&output=embed`;
  } else if (address) {
    src = `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=14&output=embed`;
  }
  if (!src) return null;

  return (
    <div className="w-full h-[360px] border border-border overflow-hidden bg-muted relative" data-testid={`map-${city || 'office'}`}>
      <div 
        className={`absolute inset-0 bg-muted flex items-center justify-center text-[10px] tracking-[0.25em] uppercase text-muted-foreground transition-opacity duration-300 z-10 pointer-events-none ${
          mapReady ? 'opacity-0' : 'opacity-100'
        }`}
      >
        Loading Map…
      </div>

      <iframe
        title={`Map - ${city || address || 'office'}`}
        src={src}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        referrerPolicy="no-referrer-when-downgrade"
        onLoad={() => setMapReady(true)}
        className={`absolute inset-0 w-full h-full block transition-opacity duration-300 ${
          mapReady ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
};

export const ContactPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [loading, setLoading] = useState(false);
  const [offices, setOffices] = useState([]);
  const [activeOffice, setActiveOffice] = useState(null);
  
  // Отметка времени загрузки страницы для проверки скорости заполнения
  const [formLoadedAt] = useState(() => Date.now());

  const [formData, setFormData] = useState({
    name: '', 
    email: '', 
    phone: '', 
    company: '', 
    subject: '', 
    message: '', 
    inquiry_type: 'general',
    honeypot_hp: '' // Невидимая ловушка для ботов
  });

  useEffect(() => {
    let alive = true;
    (async () => {      
      try {
        const currentCountry = localStorage.getItem('user-country') || 'BE';

        const cRes = await fetch(`${API}/api/contacts`);
        const cData = cRes.ok ? await cRes.json() : { contacts: [] };
        
        let fromContacts = (cData.contacts || []).map((c) => {
          const regionText = (c.region?.en || '').toUpperCase();
          const isMatch = regionText.includes(currentCountry) || (c.id && c.id.toUpperCase().includes(currentCountry));

          return {
            code: c.id,
            country: toSafeStr(getLocalized(c.region, language), language),
            city: toSafeStr(getLocalized(c.representative_name, language), language),
            address: toSafeStr(getLocalized(c.address, language), language),
            phone: c.phone || '',
            email: c.email || '',
            hours: toSafeStr(getLocalized(c.showroom_hours, language), language),
            flag: c.flag_emoji || '',
            instagram: c.instagram_url || '',
            whatsapp: c.whatsapp_url || '',
            is_primary: isMatch,
            lat: c.lat,
            lng: c.lng,
          };
        });

        if (fromContacts.length > 0) {
          if (alive) {
            fromContacts.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
            
            const hasPrimary = fromContacts.some(o => o.is_primary);
            if (!hasPrimary) {
              const beOffice = fromContacts.find(o => o.country.toLowerCase().includes('belgium') || o.code.toLowerCase().includes('be'));
              if (beOffice) beOffice.is_primary = true;
              fromContacts.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
            }

            setOffices(fromContacts);
            setActiveOffice(fromContacts[0]);
          }
          return;
        }
        
        const oRes = await fetch(`${API}/api/offices`);
        const oData = oRes.ok ? await oRes.json() : { offices: [] };
        const list = oData.offices || [];
        if (alive) {
          setOffices(list);
          const primary = list.find((o) => o.is_primary);
          setActiveOffice(primary || list[0] || null);
        }
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { alive = false; };
  }, [language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken('lead_form_completed');
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...formData, 
          recaptcha_token,
          form_loaded_at: formLoadedAt
        }),
      });
      if (!res.ok) throw new Error('Failed');
      trackEvent('lead_form_completed', { source: 'contact', inquiry_type: formData.inquiry_type });
      toast.success(toSafeStr(t?.contact?.successMsg, 'Message sent! We will get back to you soon.'));
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '', inquiry_type: 'general', honeypot_hp: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally { 
      setLoading(false); 
    }
  };

  const inquiryLabels = {
    en: { general: 'General Inquiry', product: 'Product Information', b2b: 'B2B Partnership', support: 'Support' },
    nl: { general: 'Algemene Vraag', product: 'Productinformatie', b2b: 'B2B Samenwerking', support: 'Ondersteuning' },
    ro: { general: 'Intrebare Generala', product: 'Informatii Produs', b2b: 'Parteneriat B2B', support: 'Suport' },
    ru: { general: 'Общий вопрос', product: 'Информация о продукте', b2b: 'B2B Партнерство', support: 'Поддержка' },
  };
  const il = inquiryLabels[language] || inquiryLabels.en;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const contactSchema = offices.map((o) => ({
    '@context': 'https://schema.org',
    '@type': 'FurnitureStore',
    name: `FEDYK Furniture${o.country ? ` — ${o.country}` : ''}`,
    ...(o.address ? { address: { '@type': 'PostalAddress', streetAddress: o.address, addressLocality: o.city || undefined, addressCountry: o.country || undefined } } : {}),
    ...(o.phone ? { telephone: o.phone } : {}),
    ...(o.email ? { email: o.email } : {}),
    ...(o.hours ? { openingHours: o.hours } : {}),
    ...(o.lat && o.lng ? { geo: { '@type': 'GeoCoordinates', latitude: o.lat, longitude: o.lng } } : {}),
    ...(origin ? { url: `${origin}/${language}/contact` } : {}),
    parentOrganization: { '@type': 'Organization', name: 'FEDYK Furniture' },
  }));

  const lblTitle = toSafeStr(t?.contact?.title, 'Contact');
  const lblSubtitle = toSafeStr(t?.contact?.subtitle, 'Get in touch with our team');
  const lblName = toSafeStr(t?.contact?.name, 'Name');
  const lblEmail = toSafeStr(t?.contact?.email, 'E-mail');
  const lblPhone = toSafeStr(t?.contact?.phone, 'Phone');
  const lblCompany = toSafeStr(t?.contact?.company, 'Company');
  const lblSubject = toSafeStr(t?.contact?.subject, 'Subject');
  const lblMessage = toSafeStr(t?.contact?.message, 'Message');
  const lblSend = toSafeStr(t?.contact?.send, 'Send Message');
  const lblOffices = toSafeStr(t?.contact?.offices, 'Our Offices');

  return (
    <div className="min-h-screen select-none" data-testid="contact-page">
      <SEO title={lblTitle} path="contact" jsonLd={contactSchema.length ? contactSchema : null} />
      <div className="bg-surface section-padding">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <p className="text-caption text-muted-foreground mb-4">{lblTitle}</p>
            <h1 className="text-h1 mb-4">{lblSubtitle}</h1>
          </motion.div>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <form onSubmit={handleSubmit} className="space-y-6 relative">
              
              {/* Скрытая ловушка для ботов (Honeypot Trap) */}
              <div className="hidden pointer-events-none opacity-0 absolute -left-[9999px]" aria-hidden="true">
                <input
                  type="text"
                  name="honeypot_hp"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.honeypot_hp}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">{lblName} *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} className="h-12 mt-2 select-text" required data-testid="contact-name" />
                </div>
                <div>
                  <Label htmlFor="email">{lblEmail} *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className="h-12 mt-2 select-text" required data-testid="contact-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone">{lblPhone}</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="h-12 mt-2 select-text" data-testid="contact-phone" />
                </div>
                <div>
                  <Label htmlFor="company">{lblCompany}</Label>
                  <Input id="company" name="company" value={formData.company} onChange={handleChange} className="h-12 mt-2 select-text" data-testid="contact-company" />
                </div>
              </div>
              <div>
                <Label htmlFor="inquiry_type">{il.general}</Label>
                <Select value={formData.inquiry_type} onValueChange={(v) => setFormData((p) => ({ ...p, inquiry_type: v }))}>
                  <SelectTrigger className="h-12 mt-2" data-testid="contact-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{il.general}</SelectItem>
                    <SelectItem value="product">{il.product}</SelectItem>
                    <SelectItem value="b2b">{il.b2b}</SelectItem>
                    <SelectItem value="support">{il.support}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subject">{lblSubject} *</Label>
                <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} className="h-12 mt-2 select-text" required data-testid="contact-subject" />
              </div>
              <div>
                <Label htmlFor="message">{lblMessage} *</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={6} className="mt-2 select-text" required data-testid="contact-message" />
              </div>
              <Button type="submit" className="btn-primary w-full md:w-auto" disabled={loading} data-testid="contact-submit">
                {loading ? '...' : lblSend}
                <Send className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>

          <div>
            <h3 className="text-h3 mb-6">{lblOffices}</h3>

            {activeOffice && (
              <motion.div
                key={activeOffice.code}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-8"
              >
                <OfficeMap
                  key={`${activeOffice.lat}-${activeOffice.lng}-${activeOffice.address}`}
                  lat={activeOffice.lat}
                  lng={activeOffice.lng}
                  city={activeOffice.city || activeOffice.country}
                  address={activeOffice.address}
                />
              </motion.div>
            )}

            <div className="space-y-4">
              {offices.map((office, idx) => {
                const isActive = activeOffice?.code === office.code;
                return (
                  <motion.div
                    key={office.code || idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    onClick={() => setActiveOffice(office)}
                    className={`p-5 border cursor-pointer transition-all ${
                      isActive
                        ? 'border-foreground bg-surface shadow-sm'
                        : 'border-border hover:border-foreground/30'
                    }`}
                    data-testid={`office-${office.code}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className={`text-base ${isActive ? 'font-bold' : 'font-medium'}`}>
                        {office.country} {office.city ? `— ${office.city}` : ''}
                      </h4>
                      {office.is_primary && <Star className="w-4 h-4 fill-foreground" />}
                    </div>
                    <div className={`space-y-2 text-sm ${isActive ? '' : 'text-muted-foreground'}`}>
                      {office.address && (
                        <div className="flex gap-3">
                          <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
                          <span>{office.address}</span>
                        </div>
                      )}
                      {office.phone && (
                        <div className="flex gap-3">
                          <Phone className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
                          <a href={`tel:${office.phone}`} className="hover:text-foreground">{office.phone}</a>
                        </div>
                      )}
                      {office.email && (
                        <div className="flex gap-3">
                          <Mail className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
                          <a href={`mailto:${office.email}`} className="hover:text-foreground">{office.email}</a>
                        </div>
                      )}
                      {office.hours && (
                        <div className="flex gap-3">
                          <Clock className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
                          <span>{office.hours}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
